import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { auth } from "@/lib/api";
import { strings } from "@/lib/strings";

import logoImg from "@/assets/logo.png";
import { Link } from "react-router-dom";

/**
 * Completing onboarding (docs/02, user lifecycle).
 *
 * The volunteer chooses their own password and signs the confidentiality
 * agreement. Nobody else ever knows their password, which is what makes the
 * audit log meaningful.
 */
export function SignupPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && password !== confirm;
  const ready = password.length >= 12 && password === confirm && accepted && token;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await auth.completeSignup(token, password, accepted);
      navigate("/login", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : strings.errors.generic);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-slate-50 px-4 py-8">
      <Card className="w-full max-w-md shadow-md">
        <CardBody className="space-y-4">
          <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
            <Link to="/">
              <img
                src={logoImg}
                alt="EKSUTH Logo"
                className="h-12 w-12 rounded-full border border-emerald-600/30 object-contain shadow-sm"
              />
            </Link>
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-tight">
                {strings.auth.setPassword}
              </h1>
              <p className="text-xs text-slate-500">
                Ekiti State University Teaching Hospital &bull; Volunteer Onboarding
              </p>
            </div>
          </div>

          {!token && (
            <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-900">
              This link is missing its token. Ask the Data Manager for a new one.
            </p>
          )}

          <form onSubmit={submit} className="space-y-4">
            <Field
              id="password"
              label={strings.auth.password}
              hint={strings.auth.passwordHint}
              required
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                invalid={tooShort}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              {tooShort && (
                <p className="text-xs text-blocking">At least 12 characters.</p>
              )}
            </Field>

            <Field id="confirm" label="Type it again" required>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                invalid={mismatch}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
              {mismatch && <p className="text-xs text-blocking">These do not match.</p>}
            </Field>

            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
              />
              <span>{strings.auth.confidentiality}</span>
            </label>

            {error && (
              <p role="alert" className="text-sm text-blocking">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" loading={busy} disabled={!ready}>
              Finish setting up my account
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
